import { useQuery } from '@tanstack/react-query'

import { fetchStockMovements, fetchStockOnHand } from '../api'
import type { MovementType, StockLevelListQuery, StockMovementQuery } from '../types'
import { inventoryKey } from './use-inventory'

/**
 * One page of stock levels — each item at each location — for the Overview tab.
 * `null` is every location.
 *
 * Under `stock`, so whatever moves stock and refreshes the levels refreshes this.
 */
export function useStockOnHand(locationId: string | null, page: number, pageSize: number) {
  // Left off when unset: the service refuses a param it does not know, `''` included.
  const query: StockLevelListQuery = {
    limit: pageSize,
    offset: page * pageSize,
    ...(locationId ? { locationId } : {}),
  }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'stock', 'list', query],
    queryFn: () => fetchStockOnHand(query),
    placeholderData: (previous) => previous,
  })

  return {
    levels: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/** What the ledger is narrowed to. `null` is "no filter". */
export interface MovementFilters {
  locationId: string | null
  type: MovementType | null
}

/** One page of the stock ledger, newest first. */
export function useStockMovements(
  { locationId, type }: MovementFilters,
  page: number,
  pageSize: number,
) {
  const query: StockMovementQuery = {
    limit: pageSize,
    offset: page * pageSize,
    ...(locationId ? { locationId } : {}),
    ...(type ? { type } : {}),
  }

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...inventoryKey, 'movements', query],
    queryFn: () => fetchStockMovements(query),
    placeholderData: (previous) => previous,
  })

  return {
    movements: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}
